/**
 * User Management Service
 *
 * Provides robust user management with race condition prevention.
 * Handles duplicate user record creation during concurrent auth events.
 */

import { API, graphqlOperation } from "aws-amplify";
import { listUsers, getUser } from "../graphql/queries";
import { createUser, updateUser } from "../graphql/mutations";

/**
 * Fetches a user by email address (case-insensitive)
 * @param {string} email - The email address to search for
 * @returns {Promise<Object|null>} - The user object or null if not found
 */
export const fetchUserByEmail = async (email) => {
  if (!email) {
    console.error("fetchUserByEmail: Email is required");
    return null;
  }

  // Normalize email to lowercase for consistent querying
  const normalizedEmail = email.toLowerCase().trim();

  try {
    // Try exact match first (case-sensitive GraphQL filter)
    let response = await API.graphql(
      graphqlOperation(listUsers, {
        filter: { email: { eq: normalizedEmail } },
        limit: 100, // Limit to prevent huge queries
      }),
    );

    // If no results with exact match, try with original email (in case it's stored differently)
    if (
      !response?.data?.listUsers?.items ||
      response.data.listUsers.items.length === 0
    ) {
      console.log(
        `[fetchUserByEmail] No exact match for ${normalizedEmail}, trying original case: ${email}`,
      );
      response = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { email: { eq: email } },
          limit: 100,
        }),
      );
    }

    // If still no results, try fetching a larger set and filtering client-side
    // This handles cases where email might be stored with different casing
    if (
      !response?.data?.listUsers?.items ||
      response.data.listUsers.items.length === 0
    ) {
      console.log(
        `[fetchUserByEmail] No match with filters, trying broader search...`,
      );
      // Fetch recent users and filter client-side (last 1000 users by updatedAt)
      // This is a fallback for edge cases
      const broadResponse = await API.graphql(
        graphqlOperation(listUsers, {
          limit: 1000,
        }),
      );
      const allItems = broadResponse?.data?.listUsers?.items || [];
      // Filter client-side for matching email
      const clientFiltered = allItems.filter(
        (user) =>
          user.email && user.email.toLowerCase().trim() === normalizedEmail,
      );
      if (clientFiltered.length > 0) {
        console.log(
          `[fetchUserByEmail] Found ${clientFiltered.length} user(s) via client-side filtering`,
        );
        response = { data: { listUsers: { items: clientFiltered } } };
      }
    }

    const items = response?.data?.listUsers?.items || [];

    // Case-insensitive email comparison (normalize both to lowercase)
    const matchingUsers = items.filter(
      (user) =>
        user.email && user.email.toLowerCase().trim() === normalizedEmail,
    );

    if (matchingUsers.length === 0) {
      return null;
    }

    // If multiple users with same email, prefer:
    // 1. User with activePricelistID (has pricelist assigned)
    // 2. Most recently updated user
    // 3. Most recently created user
    if (matchingUsers.length > 1) {
      console.warn(
        `[fetchUserByEmail] ⚠️ DUPLICATE USERS DETECTED: Found ${matchingUsers.length} users with email ${email}. IDs:`,
        matchingUsers.map((u) => ({
          id: u.id,
          activePricelistID: u.activePricelistID,
          updatedAt: u.updatedAt,
          createdAt: u.createdAt,
        })),
      );
      console.warn(
        `[fetchUserByEmail] This can cause issues with pricelist assignments. Consider deleting duplicate users.`,
      );

      // Sort: users with activePricelistID first, then by updatedAt, then by createdAt
      matchingUsers.sort((a, b) => {
        // Prefer users with activePricelistID
        const aHasPricelist = !!a.activePricelistID;
        const bHasPricelist = !!b.activePricelistID;
        if (aHasPricelist !== bHasPricelist) {
          // Return -1 to put A first if A has pricelist, 1 to put B first if B has pricelist
          return aHasPricelist ? -1 : 1;
        }

        // Then by most recently updated
        const aUpdated = new Date(a.updatedAt || 0);
        const bUpdated = new Date(b.updatedAt || 0);
        if (aUpdated.getTime() !== bUpdated.getTime()) {
          return bUpdated - aUpdated;
        }

        // Finally by most recently created
        const aCreated = new Date(a.createdAt || 0);
        const bCreated = new Date(b.createdAt || 0);
        return bCreated - aCreated;
      });

      console.log(
        `[fetchUserByEmail] Selected user ID: ${
          matchingUsers[0].id
        } (has pricelist: ${!!matchingUsers[0].activePricelistID})`,
      );
    }

    return matchingUsers[0];
  } catch (error) {
    console.error("Error fetching user by email:", error);
    throw error;
  }
};

/**
 * Creates a new user in the database
 * @param {Object} userData - The user data to create
 * @returns {Promise<Object>} - The created user object
 */
export const createUserRecord = async (userData) => {
  try {
    const response = await API.graphql(
      graphqlOperation(createUser, { input: userData }),
    );
    return response.data.createUser;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

/**
 * Updates an existing user record
 * @param {Object} userData - The user data to update (must include id)
 * @returns {Promise<Object>} - The updated user object
 */
export const updateUserRecord = async (userData) => {
  try {
    console.log("DEBUG updateUserRecord called:", {
      id: userData.id,
      hasCreatedByUserId: !!userData.createdByUserId,
      createdByUserId: userData.createdByUserId,
      allFields: userData,
    });
    const response = await API.graphql(
      graphqlOperation(updateUser, { input: userData }),
    );
    console.log("DEBUG updateUserRecord result:", response.data.updateUser);
    return response.data.updateUser;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

/**
 * Updates the user's lastLogin timestamp while preserving createdByUserId
 * @param {string} userId - The user ID to update
 * @returns {Promise<Object>} - The updated user object
 */
export const updateLastLogin = async (userId) => {
  try {
    // First, fetch the existing user to preserve createdByUserId
    const existingUser = await fetchUserById(userId);

    const response = await API.graphql(
      graphqlOperation(updateUser, {
        input: {
          id: userId,
          lastLogin: new Date().toISOString(),
          // Preserve createdByUserId if it exists
          ...(existingUser?.createdByUserId && {
            createdByUserId: existingUser.createdByUserId,
          }),
        },
      }),
    );
    return response.data.updateUser;
  } catch (error) {
    console.error("Error updating lastLogin:", error);
    throw error;
  }
};

/**
 * Fetches a user by ID
 * @param {string} userId - The user ID to fetch
 * @returns {Promise<Object|null>} - The user object or null if not found
 */
export const fetchUserById = async (userId) => {
  if (!userId) {
    console.error("fetchUserById: User ID is required");
    return null;
  }

  try {
    const response = await API.graphql(
      graphqlOperation(getUser, { id: userId }),
    );
    return response.data.getUser || null;
  } catch (error) {
    console.error("Error fetching user by ID:", error);
    return null;
  }
};

/**
 * Checks if an error is a duplicate email error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a duplicate email error
 */
const isDuplicateEmailError = (error) => {
  const errorMessage = error.message || "";
  const errorString = JSON.stringify(error);

  return (
    errorMessage.includes("email") ||
    errorMessage.includes("exists") ||
    errorMessage.includes("duplicate") ||
    errorString.includes("email") ||
    errorString.includes("already exists")
  );
};

/**
 * Gets or creates a user with race condition prevention
 *
 * This function implements a robust pattern to prevent duplicate user records:
 * 1. First, it queries for an existing user by email
 * 2. If found, updates the lastLogin timestamp and returns the existing user
 * 3. If not found, attempts to create a new user
 * 4. If create fails due to duplicate (race condition), retries fetching the user
 * 5. Implements retry logic up to 3 attempts
 *
 * @param {Object} userData - The user data containing at minimum an email
 * @param {string} userData.email - The user's email address
 * @param {string} [userData.username] - The user's username (defaults to email)
 * @param {number} [userData.searchesRemaining] - Default searches remaining (default: 100)
 * @returns {Promise<Object>} - The existing or newly created user object
 */
export const getOrCreateUser = async (userData) => {
  const { email, createdByUserId } = userData;

  if (!email) {
    throw new Error("Email is required for getOrCreateUser");
  }

  // Normalize email to lowercase to prevent duplicates
  const normalizedEmail = email.toLowerCase().trim();

  const MAX_RETRIES = 3;
  const newUserData = {
    username: (userData.username || normalizedEmail).toLowerCase().trim(),
    email: normalizedEmail,
    searchesRemaining: userData.searchesRemaining ?? 100,
    // DEBUG: Log when createdByUserId is provided
    ...(createdByUserId && { createdByUserId }),
  };

  console.log("DEBUG getOrCreateUser:", {
    originalEmail: email,
    normalizedEmail: normalizedEmail,
    createdByUserId,
    hasCreatedByUserId: !!createdByUserId,
    newUserData,
  });

  // Step 1: FIRST - Query ALL users with this email to see the full picture
  console.log(
    `[getOrCreateUser] 🔍 Searching for ALL users with email: ${normalizedEmail}`,
  );

  let allMatchingUsers = [];
  try {
    const allUsersResponse = await API.graphql(
      graphqlOperation(listUsers, { limit: 10000 }),
    );
    const allUsers = allUsersResponse?.data?.listUsers?.items || [];
    allMatchingUsers = allUsers.filter(
      (u) => u.email && u.email.toLowerCase().trim() === normalizedEmail,
    );

    console.log(
      `[getOrCreateUser] 📊 Found ${allMatchingUsers.length} user(s) with email ${normalizedEmail}:`,
    );
    allMatchingUsers.forEach((u, idx) => {
      console.log(`  [${idx}] ID: ${u.id}`);
      console.log(`       activePricelistID: ${u.activePricelistID || "NULL"}`);
      console.log(`       createdByUserId: ${u.createdByUserId || "NULL"}`);
      console.log(`       createdAt: ${u.createdAt}`);
      console.log(`       updatedAt: ${u.updatedAt}`);
    });
  } catch (allUsersError) {
    console.error(`[getOrCreateUser] Error fetching all users:`, allUsersError);
  }

  // Step 2: Query for existing user by email (using normalized email)
  // Add retries with delays to handle eventual consistency
  let existingUser = null;
  for (let retry = 0; retry < 3; retry++) {
    existingUser = await fetchUserByEmail(normalizedEmail);
    if (existingUser) {
      break;
    }
    // If not found and this isn't the last retry, wait a bit and try again
    if (retry < 2) {
      console.log(
        `[getOrCreateUser] User not found on attempt ${
          retry + 1
        }, retrying in 500ms...`,
      );
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  if (existingUser) {
    console.log("DEBUG: Found existing user:", {
      id: existingUser.id,
      email: existingUser.email,
      createdByUserId: existingUser.createdByUserId,
      activePricelistID: existingUser.activePricelistID,
    });

    // Check if we're returning the user with the pricelist
    const userWithPricelist = allMatchingUsers.find((u) => u.activePricelistID);
    if (userWithPricelist && userWithPricelist.id !== existingUser.id) {
      console.error(`[getOrCreateUser] ⚠️⚠️⚠️ WRONG USER SELECTED! ⚠️⚠️⚠️`);
      console.error(`  Selected: ${existingUser.id} (NO pricelist)`);
      console.error(
        `  Should be: ${userWithPricelist.id} (HAS pricelist: ${userWithPricelist.activePricelistID})`,
      );
      console.error(`  RETURNING THE USER WITH THE PRICELIST INSTEAD!`);
      existingUser = userWithPricelist;
    }

    // User exists - update lastLogin and return
    try {
      await updateLastLogin(existingUser.id);
    } catch (error) {
      console.warn(
        "Failed to update lastLogin, continuing with existing user:",
        error,
      );
    }
    return existingUser;
  }

  console.warn(
    `[getOrCreateUser] ⚠️ NO EXISTING USER FOUND for ${normalizedEmail} after 3 attempts. Proceeding to create...`,
  );

  // Step 2: User doesn't exist, attempt to create with retry on conflict
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // CRITICAL: Final check right before creating (race condition protection)
      // Add a small delay to allow any pending writes to complete
      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second
      }

      const finalCheckUser = await fetchUserByEmail(normalizedEmail);
      if (finalCheckUser) {
        console.log(
          `[getOrCreateUser] ✓ User found on final check (attempt ${
            attempt + 1
          }), returning existing user:`,
          finalCheckUser.id,
        );
        return finalCheckUser;
      }

      console.log(
        `[getOrCreateUser] No user found after final check, proceeding to create (attempt ${
          attempt + 1
        })`,
      );

      // FINAL SAFETY CHECK: Query all users to absolutely ensure no duplicate exists
      // This is expensive but necessary to prevent duplicates
      try {
        const allUsersResponse = await API.graphql(
          graphqlOperation(listUsers, { limit: 10000 }),
        );
        const allUsers = allUsersResponse?.data?.listUsers?.items || [];
        const duplicateCheck = allUsers.find(
          (u) => u.email && u.email.toLowerCase().trim() === normalizedEmail,
        );

        if (duplicateCheck) {
          console.error(
            `[getOrCreateUser] ⚠️⚠️⚠️ CRITICAL: Found duplicate user ${duplicateCheck.id} with email ${normalizedEmail} in full user list!`,
          );
          console.error(
            `[getOrCreateUser] This should not happen - fetchUserByEmail should have found this user`,
          );
          console.error(
            `[getOrCreateUser] Returning existing user instead of creating duplicate`,
          );
          return duplicateCheck;
        }
        console.log(
          `[getOrCreateUser] ✓ Final safety check passed: No duplicates found in ${allUsers.length} users`,
        );
      } catch (safetyCheckError) {
        console.error(
          `[getOrCreateUser] Error in final safety check, proceeding with caution:`,
          safetyCheckError,
        );
        // Continue with creation but log the error
      }

      const createInput = {
        ...newUserData,
      };

      const newUser = await createUserRecord(createInput);
      console.log(
        `[getOrCreateUser] User created successfully on attempt ${
          attempt + 1
        }:`,
        newUser.id,
      );

      // Wait longer for database to commit and replicate
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // CRITICAL: Verify the user was actually created and check for duplicates
      let verifyUser = null;
      for (let verifyAttempt = 0; verifyAttempt < 3; verifyAttempt++) {
        verifyUser = await fetchUserByEmail(normalizedEmail);
        if (verifyUser) {
          break;
        }
        if (verifyAttempt < 2) {
          console.log(
            `[getOrCreateUser] Verification attempt ${
              verifyAttempt + 1
            } failed, retrying...`,
          );
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (verifyUser) {
        if (verifyUser.id !== newUser.id) {
          console.error(
            `[getOrCreateUser] ⚠️⚠️⚠️ CRITICAL DUPLICATE DETECTED ⚠️⚠️⚠️`,
          );
          console.error(
            `[getOrCreateUser] Created user ${newUser.id} but found DIFFERENT user ${verifyUser.id} with same email ${normalizedEmail}`,
          );
          console.error(
            `[getOrCreateUser] This means a duplicate was created! Returning existing user ${verifyUser.id}`,
          );
          console.error(
            `[getOrCreateUser] ACTION REQUIRED: Delete duplicate user ${newUser.id} from database`,
          );
          // Return the existing user (the one that was there first)
          return verifyUser;
        } else {
          console.log(
            `[getOrCreateUser] ✓ Verified: Created user ${newUser.id} matches database`,
          );
        }
      } else {
        console.error(
          `[getOrCreateUser] ⚠️ Created user ${newUser.id} but could not verify it exists in database after 3 attempts`,
        );
        console.error(
          `[getOrCreateUser] This might indicate a database issue or the user was not actually created`,
        );
      }

      return newUser;
    } catch (error) {
      // Check if this is a duplicate email error (race condition)
      if (isDuplicateEmailError(error)) {
        console.warn(
          `Duplicate email detected on attempt ${
            attempt + 1
          }, fetching existing user...`,
        );

        // Another process created the user, fetch and return it (using normalized email)
        const retryUser = await fetchUserByEmail(normalizedEmail);
        if (retryUser) {
          return retryUser;
        }

        // If we still didn't find it, continue to retry
        if (attempt < MAX_RETRIES - 1) {
          console.log(`Retrying... (attempt ${attempt + 2}/${MAX_RETRIES})`);
          // Small delay to allow concurrent operations to complete
          await new Promise((resolve) =>
            setTimeout(resolve, 100 * (attempt + 1)),
          );
          continue;
        }
      }

      // If it's not a duplicate error or we've exhausted retries, throw
      console.error(`Failed to create user on attempt ${attempt + 1}:`, error);
      throw error;
    }
  }

  throw new Error("Failed to create user after multiple attempts");
};

/**
 * Fetches all users from the database
 * @param {Object} [userRecord] - The current user record (for filtering)
 * @returns {Promise<Array>} - Array of user objects
 */
export const fetchUsers = async (userRecord) => {
  try {
    let filter = {};

    // If userRecord is provided and has an ID, filter to only show users this user has access to
    if (userRecord?.id) {
      filter = {
        or: [
          { createdByUserId: { eq: userRecord.id } },
          { priceListInvites: { contains: userRecord.id } },
        ],
      };
    }

    const response = await API.graphql(
      graphqlOperation(listUsers, {
        filter,
        limit: 10000,
      }),
    );

    const users = response?.data?.listUsers?.items || [];

    // Filter out soft-deleted users
    return users.filter((user) => user.createdByUserId !== "deleted");
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

/**
 /**
  * Extract provider ID from various possible field names
  * @param {Object} providerRecord - The provider record object
  * @returns {string|null} - The extracted ID or null if not found
  */
const extractProviderId = (providerRecord) => {
  if (!providerRecord || typeof providerRecord !== "object") {
    return null;
  }

  // Try common ID field variations
  const possibleIdFields = [
    "id",
    "userID",
    "userId",
    "providerId",
    "providerID",
    "user.id",
    "user.userID",
    "user.userId",
  ];

  for (const field of possibleIdFields) {
    if (field.includes(".")) {
      // Handle nested fields like 'user.id'
      const [parent, child] = field.split(".");
      if (providerRecord[parent] && providerRecord[parent][child]) {
        console.log(`[extractProviderId] Found ID in nested field '${field}':`, providerRecord[parent][child]);
        return providerRecord[parent][child];
      }
    } else if (providerRecord[field]) {
      console.log(`[extractProviderId] Found ID in field '${field}':`, providerRecord[field]);
      return providerRecord[field];
    }
  }

  console.warn("[extractProviderId] No valid ID found in providerRecord");
  return null;
};

/**
 * Create standardized response format
 * @param {boolean} success - Whether the operation was successful
 * @param {Array} data - The data to return
 * @param {string} error - Error type if any
 * @param {string} message - Error or success message
 * @returns {Object} Standardized response object
 */
const createResponse = (success, data = [], error = null, message = "") => ({
  success,
  data,
  error,
  message,
  timestamp: new Date().toISOString(),
});

/**
 * Fetches a user's customers using the explicit relationship field
 *
 * This function uses the new `customers` relationship field defined in the schema.
 * It:
 * 1. Fetches the provider user
 * 2. Retrieves their customers from the relationship field
 * 3. Filters out soft-deleted users and the provider themselves
 *
 * Enhanced to handle missing providerRecord.id with fallback extraction
 *
 * @param {Object} providerRecord - The provider/user record object
 * @param {number} [limit=10000] - Maximum number of users to fetch
 * @returns {Promise<Object>} Standardized response with success, data, error, and message
 */
export const fetchCustomersForProvider = async (
  providerRecord,
  limit = 10000,
) => {
  try {
    console.log("═".repeat(80));
    console.log("[fetchCustomersForProvider] === DEBUG START ===");
    console.log("[fetchCustomersForProvider] Called with providerRecord:", providerRecord);
    console.log("[fetchCustomersForProvider] providerRecord type:", typeof providerRecord);
    console.log("[fetchCustomersForProvider] providerRecord keys:", Object.keys(providerRecord || {}));
    console.log("[fetchCustomersForProvider] limit:", limit);
    console.log("═".repeat(80));

    // Enhanced validation with fallback ID extraction
    const providerId = extractProviderId(providerRecord);

    if (!providerId) {
      console.error(
        "[fetchCustomersForProvider] ❌ No valid provider ID found in providerRecord:",
        providerRecord,
      );
      console.error(
        "[fetchCustomersForProvider] Attempting to extract from available fields...",
      );

      // Log available fields for debugging
      const availableFields = Object.keys(providerRecord || {});
      console.error(
        "[fetchCustomersForProvider] Available fields in providerRecord:",
        availableFields,
      );

      return createResponse(
        false,
        [],
        "MISSING_PROVIDER_ID",
        `Unable to extract provider ID from record. Available fields: ${availableFields.join(", ")}`,
      );
    }

   console.log(
      "[fetchCustomersForProvider] Successfully extracted provider ID:",
      providerId,
    );
    console.log(
      "[fetchCustomersForProvider] Provider ID type:",
      typeof providerId,
    );

    // Approach 1: Try using the customers relationship field (if it exists)
    console.log(
      "[fetchCustomersForProvider] Attempting to fetch provider user...",
    );
    console.log(
      "[fetchCustomersForProvider] GraphQL query parameters:",
      { id: providerId },
    );
    const response = await API.graphql(
      graphqlOperation(getUser, { id: providerId }),
    );

    console.log(
      "[fetchCustomersForProvider] GraphQL response received:",
      response,
    );

    const provider = response?.data?.getUser;

    if (!provider) {
      console.error(
        "[fetchCustomersForProvider] ❌ Provider not found in response.",
      );
      console.error(
        "[fetchCustomersForProvider] Full response:",
        response,
      );
      return createResponse(
        false,
        [],
        "PROVIDER_NOT_FOUND",
        `Provider with ID ${providerId} not found in database`,
      );
    }

    console.log(
      "[fetchCustomersForProvider] ✅ Provider fetched successfully.",
    );
    console.log(
      "[fetchCustomersForProvider] Provider ID from response:",
      provider.id,
    );
    console.log(
      "[fetchCustomersForProvider] Provider email:",
      provider.email,
    );
    console.log(
      "[fetchCustomersForProvider] Provider username:",
      provider.username,
    );
    console.log(
      "[fetchCustomersForProvider] Provider isProvider:",
      provider.isProvider,
    );
    console.log(
      "[fetchCustomersForProvider] Provider ID type:",
      typeof providerId,
    );

    if (!provider) {
      console.error(
        "[fetchCustomersForProvider] Provider not found in response.",
      );
      return createResponse(
        false,
        [],
        "PROVIDER_NOT_FOUND",
        `Provider with ID ${providerId} not found in database`,
      );
    }

    console.log(
      "[fetchCustomersForProvider] Provider fetched successfully. Provider ID:",
      provider.id,
    );

    // Check if customers relationship exists and is populated
    console.log(
      "[fetchCustomersForProvider] Checking customers relationship...",
    );
    console.log(
      "[fetchCustomersForProvider] provider.customers:",
      provider.customers,
    );
    console.log(
      "[fetchCustomersForProvider] typeof provider.customers:",
      typeof provider.customers,
    );
    console.log(
      "[fetchCustomersForProvider] Array.isArray(provider.customers):",
      Array.isArray(provider.customers),
    );

    if (provider.customers && Array.isArray(provider.customers)) {
      console.log(
        "[fetchCustomersForProvider] ✅ Using customers relationship field. Found",
        provider.customers.length,
        "customers.",
      );

      // Filter out soft-deleted users (createdByUserId === "deleted")
      const validCustomers = provider.customers.filter(
        (user) => user.createdByUserId !== "deleted",
      );

      console.log(
        "[fetchCustomersForProvider] Valid customers (excluding deleted):",
        validCustomers.length,
      );

      return createResponse(
        true,
        validCustomers,
        null,
        `Successfully fetched ${validCustomers.length} customers via relationship field`,
      );
    }

    // Approach 2: If customers field doesn't exist or is empty, query directly
    console.warn(
      "[fetchCustomersForProvider] ⚠️ customers field not available or is not an array.",
    );
    console.warn(
      "[fetchCustomersForProvider] Querying directly via createdByUserId filter...",
    );

    console.log(
      "[fetchCustomersForProvider] Executing direct query with filter:",
      { createdByUserId: { eq: providerId } },
      "limit:",
      limit,
    );
    const directResponse = await API.graphql(
      graphqlOperation(listUsers, {
        filter: { createdByUserId: { eq: providerId } },
        limit,
      }),
    );

    console.log(
      "[fetchCustomersForProvider] Direct query response received:",
      directResponse,
    );

    const users = directResponse?.data?.listUsers?.items || [];

    console.log(
      "[fetchCustomersForProvider] Direct query results. Total users with createdByUserId:",
      users.length,
    );

    if (users.length > 0) {
      console.log(
        "[fetchCustomersForProvider] ✅ Found",
        users.length,
        "customers via direct query",
      );
      console.log(
        "[fetchCustomersForProvider] Sample customers from direct query:",
        users.slice(0, 3).map((u) => ({
          id: u.id,
          email: u.email,
          createdByUserId: u.createdByUserId,
          username: u.username,
          isProvider: u.isProvider,
        })),
      );
      
      // Log all unique createdByUserId values to check for data consistency
      const uniqueCreatedByIds = [...new Set(users.map(u => u.createdByUserId))];
      console.log(
        "[fetchCustomersForProvider] Unique createdByUserId values in results:",
        uniqueCreatedByIds,
      );
    } else {
      console.warn(
        "[fetchCustomersForProvider] ❌ No customers found with createdByUserId:",
        providerId,
      );
      console.warn(
        "[fetchCustomersForProvider] This could mean:",
      );
      console.warn(
        "  1. No customers have been created for this provider",
      );
      console.warn(
        "  2. The providerId doesn't match any createdByUserId in the database",
      );
      console.warn(
        "  3. The filter syntax might be incorrect",
      );
    }

    // Filter out soft-deleted users
    const validCustomers = users.filter(
      (user) => user.createdByUserId !== "deleted",
    );

    console.log(
      "[fetchCustomersForProvider] Final customers after filtering (excluding deleted):",
      validCustomers.length,
    );

    if (validCustomers.length === 0) {
      console.error(
        "[fetchCustomersForProvider] ❌ No valid customers found for provider ID:",
        providerId,
      );
      return createResponse(
        false,
        [],
        "NO_CUSTOMERS_FOUND",
        `No customers found for provider ID ${providerId}`,
      );
    }

    console.log(
      "[fetchCustomersForProvider] ✅ Returning",
      validCustomers.length,
      "customers",
    );
    return createResponse(
      true,
      validCustomers,
      null,
      `Successfully fetched ${validCustomers.length} customers via direct query`,
    );
  } catch (error) {
    console.error("═".repeat(80));
    console.error("[fetchCustomersForProvider] ❌ ERROR OCCURRED");
    console.error("[fetchCustomersForProvider] Error:", error);
    console.error("[fetchCustomersForProvider] Error message:", error.message);
    console.error("[fetchCustomersForProvider] Error stack:", error.stack);
    console.error("═".repeat(80));

    return createResponse(
      false,
      [],
      "FUNCTION_ERROR",
      `Error fetching customers: ${error.message}`,
    );
  } finally {
    console.log("═".repeat(80));
    console.log("[fetchCustomersForProvider] === DEBUG END ===");
    console.log("═".repeat(80));
  }
};

/**
 * Refreshes user data from the database
 * @param {Object} authUser - The authenticated user object from Amplify
 * @returns {Promise<Object|null>} - The user record or null if not found
 */
export const refreshUserData = async (authUser) => {
  if (!authUser?.attributes?.email) {
    console.error("refreshUserData: User email is missing");
    return null;
  }

  const existingUser = await fetchUserByEmail(authUser.attributes.email);
  return existingUser || null;
};

// Export helper functions for testing
export { extractProviderId, createResponse };

export default {
  fetchUserByEmail,
  fetchUserById,
  createUserRecord,
  updateUserRecord,
  updateLastLogin,
  getOrCreateUser,
  refreshUserData,
  fetchUsers,
  fetchCustomersForProvider,
};
